import { useFormik } from 'formik';
import {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useMemo,
  useState
} from 'react';
import styled from 'styled-components';
import { colors } from '../../../component/colors';
import { DropDownWithLabel } from '../../../component/dropDown';
import { SearchableDropDownWithLabel } from '../../../component/dropDown/searchableDropDownWthLabel';
import { ImagePicker } from '../../../component/imagePicker';
import { Input } from '../../../component/input';
import { AddModal } from '../../../component/modal';
import { TextAreaWithLabel } from '../../../component/textArea';
import { useFetchAgentQuery } from '../../../store/agent/api';
import { useEditHotelMutation } from '../../../store/hotel/api';
import { IHotel } from '../../../store/hotel/interface';
import { facilities, hotelRatings, hotleTypes } from '../../../utils/module';
import { pxToRem } from '../../../utils/pxToRem';
import { showErrorToast, showSuccessToast } from '../../../utils/toast';
import { generateUrl } from '../../../utils/urlGenerator';
import useDebounce from '../../../utils/useDebounce';
import { editHotelValidation } from './validation';

type IAddHotel = {
  show?: boolean;
  setShow: Dispatch<SetStateAction<boolean>>;
  hotelItems: IHotel;
};
type FormikValues = {
  name: string;
  numberOfRooms: number;
  type: string;
  hotelRating: string;
  facilities: string[];
  agent: {
    name: string;
  };
  file: File[] | null;
  description: string;
  id: string;
};
export const EditHotel: FC<IAddHotel> = ({ setShow, hotelItems }) => {
  const [searchAgent, setSearchAgent] = useState('');
  const debounceSearchAgent = useDebounce(searchAgent, 500);
  const { data: agents } = useFetchAgentQuery(
    generateUrl({ module: 'hotel', search: debounceSearchAgent })
  );
  const [editHotel, { isLoading: isAdding }] = useEditHotelMutation();

  const agentsList = useMemo(() => {
    return (
      agents?.data.fetchedData.map((agents) => ({
        name: agents.name,
        imageUrl: agents.file.url
      })) ?? []
    );
  }, [agents]);
  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };

  const handleEditHotel = (values: FormikValues) => {
    const payload = new FormData();
    if (values.name !== hotelItems.name) {
      payload.append('name', values.name);
    }
    if (values.type !== hotelItems.type) {
      payload.append('type', values.type.toLocaleLowerCase().replace(' ', '-'));
    }
    if (values.description !== hotelItems.description) {
      payload.append('description', values.description);
    }
    const selectedAgent = agents?.data?.fetchedData.find((item) => {
      return item.name === values.agent.name;
    });
    if (selectedAgent && selectedAgent._id !== hotelItems.agent._id) {
      payload.append('agent', selectedAgent._id);
    }

    if (values.numberOfRooms !== hotelItems.noOfRooms) {
      payload.append('noOfRooms', values.numberOfRooms.toString());
    }
    if (values.file) {
      values.file.forEach((file) => {
        payload.append('file', file);
      });
    }
    if (values.facilities !== hotelItems.facilitiesAndServices) {
      for (const item of values.facilities) {
        payload.append('facilitiesAndServices', item);
      }
    }
    payload.append('id', values.id);
    editHotel(payload)
      .unwrap()
      .then((res) => {
        showSuccessToast(res.message);
        setShow(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };
  const { values, errors, touched, setFieldValue, handleChange, handleSubmit } =
    useFormik({
      initialValues: {
        name: hotelItems?.name ?? '',
        numberOfRooms: hotelItems?.noOfRooms ?? 1,
        type: hotelItems?.type ?? hotleTypes[0],
        hotelRating: hotelItems?.rating ?? hotelRatings[0],
        facilities: hotelItems.facilitiesAndServices,
        agent: { name: hotelItems?.agent?.name ?? '', index: 0 },
        file: null,
        description: hotelItems?.description ?? '',
        id: hotelItems?._id ?? ''
      },
      validationSchema: editHotelValidation,
      onSubmit: handleEditHotel
    });
  const handlRemoveFacilities = (facility: string) => {
    setFieldValue(
      'facilities',
      values.facilities.filter((item) => item !== facility)
    );
  };
  const handleSelectFacilities = (
    e: ChangeEvent<HTMLInputElement>,
    facility: string
  ) => {
    if (e.target.checked) {
      setFieldValue('facilities', [...values.facilities, facility]);
    } else {
      handlRemoveFacilities(facility);
    }
  };

  return (
    <AddModal
      title="Edit Hotel"
      disableBtn={isAdding}
      btnTxt={isAdding ? 'Loading...' : 'Update'}
      btnClick={handleSubmit}
      closeModal={() => setShow(false)}
      isLoading={isAdding}
    >
      <Container>
        <Row>
          <CustomInput
            labelText="Name"
            value={values.name}
            placeholder={'Enter name'}
            onChange={handleChange('name')}
            errorMessage={touched.name ? errors.name : ''}
          />
          <CustomDropDown
            label="Type"
            value={values.type}
            onSelect={(item) => {
              setFieldValue('type', item);
            }}
            options={hotleTypes}
          />
        </Row>
        <Row>
          <CustomInput
            labelText={'Number of rooms'}
            value={values.numberOfRooms}
            type={'number'}
            min={1}
            onChange={handleChange('numberOfRooms')}
            errorMessage={touched.numberOfRooms ? errors.numberOfRooms : ''}
          />
          <CustomDropDown
            label="Hotel rating"
            value={values.hotelRating}
            onSelect={(item) => {
              setFieldValue('hotelRating', item);
            }}
            options={hotelRatings}
          />
        </Row>
        <Row>
          <CustomDropDown
            label={'Facilities & services'}
            value={''}
            placeholder={'Select facilities & services'}
          >
            {facilities.map((item, index) => (
              <DropDownItem key={index.toString()}>
                <CheckBox
                  onChange={(e) => handleSelectFacilities(e, item)}
                  checked={values.facilities.includes(item)}
                  type={'checkbox'}
                />
                {item}
              </DropDownItem>
            ))}
          </CustomDropDown>
          <SearchableCustomDropDown
            search={searchAgent}
            setSearch={setSearchAgent}
            label="SOJI agent"
            value={values.agent.name}
            onSelect={(item, index) => {
              setFieldValue('agent', { name: item, index });
            }}
            options={agentsList}
            errorMessage={touched.agent && errors.agent ? '*required' : ''}
          />
        </Row>
        <SeletedFacilitiesContainer>
          {values.facilities.map((item, index) => (
            <SeletedFacilities
              onClick={() => handlRemoveFacilities(item)}
              key={index.toString()}
            >{`${item}  x`}</SeletedFacilities>
          ))}
        </SeletedFacilitiesContainer>
        <CustomTextAreaWithLabel
          labelText="Description"
          placeholder="Enter description"
          value={values.description}
          onChange={handleChange('description')}
          errorMessage={touched.description ? errors.description : ''}
        />
        <CustomImageUpload
          initialValue={hotelItems.file.map((item) => item.url)}
          onFilePicked={handleFilePicked}
        />
      </Container>
    </AddModal>
  );
};

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  padding-bottom: 32px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;

  @media screen and (max-width: 500px) {
    display: block;
  }
`;

const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const CustomImageUpload = styled(ImagePicker)``;

const DropDownItem = styled.div`
  background-color: transparent;
  border: none;
  text-align: left;
  width: 100%;
  margin: 16px 23px;
`;
const CheckBox = styled.input``;

const SeletedFacilitiesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;
const SeletedFacilities = styled.p`
  font-weight: bold;
  font-size: ${pxToRem(10)};
  line-height: ${pxToRem(16)} 16px;
  align-items: center;
  color: #a6b7d4;
  background-color: #ebf2fa;
  border-radius: 6px;
  padding: 4px 10px;
  margin-right: 8px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;
const CustomTextAreaWithLabel = styled(TextAreaWithLabel)`
  margin-bottom: 16px;
`;
const SearchableCustomDropDown = styled(SearchableDropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
